import React, { useState } from 'react';
import { Phone, Clock, Menu, X } from 'lucide-react';
import ContactModal from './ContactModal';
import { useTranslation } from 'react-i18next';
import { companyInfo } from '../data/mockData';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
  };
  return (
    <>
      {/* Top Bar */}
      <div className="bg-gray-100 text-gray-600 text-xs sm:text-sm py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
          <div className="flex items-center space-x-2">
            <span className="text-gray-400">👍</span>
            <span className="text-center sm:text-left">
              {t('header.topBanner')} - {companyInfo.serviceAreas.join(', ')}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4" />
            <span className="text-center sm:text-left">{companyInfo.hours}</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white shadow-sm py-4 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_react-appacific/artifacts/l8edfh5m_ap_pacific_logo.png" 
              alt="AP Pacific Inc." 
              className="h-12 sm:h-16 w-auto"
            />
          </div>

          {/* Desktop Phone Info */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="flex flex-col space-y-1">
              <div className="flex items-center space-x-2 text-gray-600">
                <Phone className="w-5 h-5" />
                <div>
                  <div className="text-xs text-gray-500">{t('header.callUsToday')}</div>
                  <a href={`tel:${companyInfo.phones[0].replace(/[^0-9]/g, '')}`} className="font-bold text-lg hover:text-blue-600">
                    {companyInfo.phones[0]}
                  </a>
                </div>
              </div>
              <div className="text-sm text-gray-600 ml-7">
                <a href={`tel:${companyInfo.phones[1].replace(/[^0-9]/g, '')}`} className="hover:text-blue-600">
                  {companyInfo.phones[1]}
                </a>
              </div>
            </div>
            {companyInfo.emergency24_7 && (
              <div className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold text-center leading-tight whitespace-nowrap">
                24/7<br/>Emergency
              </div>
            )}
          </div>

          {/* Mobile Phone */}
          <div className="md:hidden">
            <a href={`tel:${companyInfo.phones[0].replace(/[^0-9]/g, '')}`} className="flex items-center space-x-2 text-blue-500">
              <Phone className="w-5 h-5" />
              <span className="font-bold">{companyInfo.phones[0]}</span>
            </a>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-blue-500 text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            <a href="/" className="text-yellow-300 font-semibold hover:text-yellow-200 transition-colors">{t('header.menu.home')}</a>
            <a href="/about" className="hover:text-yellow-200 transition-colors">{t('header.menu.about')}</a>
            <a href="/services" className="hover:text-yellow-200 transition-colors">{t('header.menu.services')}</a>
            <a href="/commercial" className="hover:text-yellow-200 transition-colors">{t('header.menu.commercial')}</a>
            <a href="/residential" className="hover:text-yellow-200 transition-colors">{t('header.menu.residential')}</a>
            <a href="/portfolio" className="hover:text-yellow-200 transition-colors">{t('header.menu.portfolio')}</a>
            <a href="/contact" className="hover:text-yellow-200 transition-colors">{t('header.menu.contact')}</a>

            {/* Language Selector */}
            <div className="flex items-center space-x-2 border-l border-blue-400 pl-6">
              <button
                onClick={() => changeLanguage('en')}
                className={`flex items-center space-x-1 transition-colors ${
                  i18n.language === 'en' ? 'text-yellow-300' : 'text-blue-200 hover:text-white'
                }`}
                title={t('footer.languageEnglish')}
              >
                <img
                  src="https://flagcdn.com/24x18/ca.png"
                  alt={t('footer.languageEnglish')}
                  className="w-6 h-4"
                />
                <span className="text-sm">EN</span>
              </button>
              <button
                onClick={() => changeLanguage('fr')}
                className={`flex items-center space-x-1 transition-colors ${
                  i18n.language === 'fr' ? 'text-yellow-300' : 'text-blue-200 hover:text-white'
                }`}
                title={t('footer.languageFrench')}
              >
                <img
                  src="https://flagcdn.com/24x18/fr.png"
                  alt={t('footer.languageFrench')}
                  className="w-6 h-4"
                />
                <span className="text-sm">FR</span>
              </button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden flex items-center space-x-2"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            <span>{t('header.menuButton')}</span>
          </button>

          {/* Desktop Quote Button */}
          <button
            onClick={() => setIsModalOpen(true)}
            className="hidden sm:block bg-yellow-400 text-blue-900 font-bold px-4 lg:px-6 py-2 rounded-full hover:bg-yellow-300 transition-colors text-sm lg:text-base"
          >
            {t('header.quoteButton')}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-blue-400">
            <div className="flex flex-col space-y-3 mt-4">
              <a href="/" className="text-yellow-300 font-semibold hover:text-yellow-200 transition-colors">{t('header.menu.home')}</a>
              <a href="/about" className="hover:text-yellow-200 transition-colors">{t('header.menu.about')}</a>
              <a href="/services" className="hover:text-yellow-200 transition-colors">{t('header.menu.services')}</a>
              <a href="/commercial" className="hover:text-yellow-200 transition-colors">{t('header.menu.commercial')}</a>
              <a href="/residential" className="hover:text-yellow-200 transition-colors">{t('header.menu.residential')}</a>
              <a href="/portfolio" className="hover:text-yellow-200 transition-colors">{t('header.menu.portfolio')}</a>
              <a href="/contact" className="hover:text-yellow-200 transition-colors">{t('header.menu.contact')}</a>

              {/* Language Selector */}
              <div className="flex items-center justify-center space-x-4 pt-4 border-t border-blue-400">
                <button
                  onClick={() => changeLanguage('en')}
                  className={`flex items-center space-x-2 transition-colors ${
                    i18n.language === 'en' ? 'text-yellow-300' : 'text-blue-200 hover:text-white'
                  }`}
                >
                  <img
                    src="https://flagcdn.com/24x18/ca.png"
                    alt={t('footer.languageEnglish')}
                    className="w-6 h-4"
                  />
                  <span>{t('footer.languageEnglish')}</span>
                </button>
                <button
                  onClick={() => changeLanguage('fr')}
                  className={`flex items-center space-x-2 transition-colors ${
                    i18n.language === 'fr' ? 'text-yellow-300' : 'text-blue-200 hover:text-white'
                  }`}
                >
                  <img
                    src="https://flagcdn.com/24x18/fr.png"
                    alt={t('footer.languageFrench')}
                    className="w-6 h-4"
                  />
                  <span>{t('footer.languageFrench')}</span>
                </button>
              </div>

              <button
                onClick={() => {
                  setIsModalOpen(true);
                  setIsMobileMenuOpen(false);
                }}
                className="bg-yellow-400 text-blue-900 font-bold px-6 py-3 rounded-full hover:bg-yellow-300 transition-colors w-full mt-4"
              >
                {t('header.quoteButton')}
              </button>
            </div>
          </div>
        )}
      </nav>
      
      <ContactModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </>
  );
};

export default Header;